import { StrengthSetsSummary } from "./StrengthSetsSummary";
import React, { useEffect, useState } from "react";
import { Dumbbell } from "lucide-react";
import { coachGetAthleteStrengthSets } from "../../lib/api";

/**
 * CoachStrengthLogView — read-only panel showing what the athlete actually
 * logged for the strength/mobility exercises in this workout. Rendered inside
 * the WorkoutEditor when the program is assigned to a specific athlete
 * (1:1 programs only; generic library programs have no athlete_id).
 *
 * Props:
 *   - athleteId:   coaching_athlete_id of the program owner
 *   - programId:   program.id
 *   - workoutId:   workout.id we're editing
 *   - blocks:      the workout's strength_blocks (to render exercise rows even
 *                   when the athlete hasn't logged anything yet)
 */
export default function CoachStrengthLogView({
  athleteId,
  programId,
  workoutId,
  blocks,
}) {
  const [logsByName, setLogsByName] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!athleteId || !workoutId) {
      setLoading(false);
      return;
    }
    coachGetAthleteStrengthSets(athleteId, {
      program_id: programId,
      workout_id: workoutId,
    })
      .then((logs) => {
        if (!alive) return;
        // Index by exercise id, by logged name AND by the swapped-from
        // name so a swapped exercise still lines up with its prescription.
        const map = {};
        const norm = (v) => (v || "").toLowerCase().trim().replace(/\s+/g, " ");
        for (const log of logs || []) {
          if (log.exercise_id) map[`id:${log.exercise_id}`] = log;
          map[log.exercise_name_norm || norm(log.exercise_name)] = log;
          if (log.swapped_from_name) map[norm(log.swapped_from_name)] = log;
        }
        setLogsByName(map);
      })
      .catch(() => alive && setLogsByName({}))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [athleteId, programId, workoutId]);

  if (loading) return null;
  if (!blocks?.length) return null;

  const anyLogged = Object.keys(logsByName).length > 0;

  return (
    <div
      className="mt-4 border border-[var(--tir-navy)]/30 bg-[#eef2f6] p-3"
      data-testid="coach-strength-log-view"
    >
      <div className="flex items-center gap-2 mb-2">
        <Dumbbell size={13} className="text-[var(--tir-navy)]" />
        <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)]">
          Athlete logged
        </span>
        {!anyLogged && (
          <span className="text-[11px] italic text-[var(--tir-muted)] normal-case tracking-normal">
            — nothing logged yet
          </span>
        )}
      </div>
      {anyLogged && (
        <div className="space-y-2.5">
          {blocks.map((block) => (
            <div key={block.id}>
              {block.label && (
                <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                  {block.label}
                </div>
              )}
              {(block.exercises || []).map((ex) => {
                const norm = (ex.name || "").toLowerCase().trim().replace(/\s+/g, " ");
                const log = (ex.id && logsByName[`id:${ex.id}`]) || logsByName[norm];
                if (!log) return null;
                return (
                  <StrengthSetsSummary
                    key={ex.id || ex.name}
                    entries={[{ exercise_name: log.exercise_name || ex.name, swapped_from_name: log.swapped_from_name, sets: log.sets }]}
                    testid={`coach-strength-log-${ex.id || norm}`}
                    compact
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
